import Constants from 'api-constants'
import ConfigHelper from 'framework/helpers/config'
import DataHelper from 'framework/helpers/data'
import { from, of, throwError } from 'rxjs'
import { catchError, mergeMap, switchMap } from 'rxjs/operators'
import Fetch from 'services/ufinity/fetch'
import VoltError from 'VoltError'
import {
    tvServiceFactory,
    ufinityProductMappingFactory,
    ufinityOffersFactory,
    transactionListFactory,
} from '../factories'
import { generateJwtForOtt } from '../helpers/jwt'
import CommonApi from 'services/common'
import { UfinityProduct } from '../models'

/** Class for working with API methods of OTT Gateway
 * @class
 */
export default class GatewayApi extends Fetch {
    constructor(config, otherApis = {}) {
        super(config, otherApis)
        this.config = config
    }

    _ufinityConfig = ConfigHelper.getInstance().getConfig('ufinity')
    _apiUrl = ConfigHelper.getInstance().getConfig('urls').gatewayApiUrl

    /**
     * Different service type needed by the OTT Gateway
     */
    static SERVICE_TYPE = {
        ESSOID: 'ESSOID',
        HUBID: 'HUBID',
        TVID: 'TVID',
    }

    /** @type {String} user's device id */
    _deviceId = this.config.uniqueDeviceId

    /**
     * Returns if the account is a TV Account or by default a ESSOID
     * @warn Heuristic to avoid important extra work upper layer, not ideal but it's OK for now
     *       Needs to be done otherwise it will fail
     * @returns {Boolean} true if TVID account (contains TV in the name) false otherwise (ESSOID)
     */
    static _isTvIDAccount(tvid = '') {
        return tvid && tvid.toLowerCase().includes('tv')
    }

    /**
     * Returns profile data from storage via {@link DataHelper}
     * @returns {ProfileData}
     */
    _getUserProfileData() {
        return {
            essoid: DataHelper.getInstance().getData(DataHelper.STORE_KEY.ESSOID),
            tvid: DataHelper.getInstance().getData(DataHelper.STORE_KEY.ACCOUNT_ID),
            hubId: DataHelper.getInstance().getData(DataHelper.STORE_KEY.HUBID),
        }
    }

    /**
     * Gets proper values depending on service type
     * @returns {Service}
     * @private
     */
    _getServiceIdAndType({ tvid, essoid, hubId }) {
        // /!\ Very Important /!\
        // The ordering has a big importance due to Account structure composition
        // For one ESSO ID - There is ONE HUB ID
        // For one HUB ID - There could MULTIPLE TVIDs
        // Each TVID is a completely different account from Nagra Standpoint
        // This is why when resetting a PIN we SHALL use the TVID as the operation IS DONE on Nagra Account at the end
        if (tvid)
            return {
                serviceType: GatewayApi._isTvIDAccount(tvid)
                    ? GatewayApi.SERVICE_TYPE.TVID
                    : GatewayApi.SERVICE_TYPE.ESSOID,
                serviceId: tvid,
            }
        if (essoid) return { serviceType: GatewayApi.SERVICE_TYPE.ESSOID, serviceId: essoid }
        return { serviceType: GatewayApi.SERVICE_TYPE.HUBID, serviceId: hubId }
    }

    /**
     * Returns JWT
     * @param {Service} service
     * @returns {(String|undefined)} returns JWT or undefined if there is no "iss" or "privateKey"
     * @private
     */
    _generateJwt({ serviceType, serviceId }) {
        if (this._ufinityConfig.jwt.isUseJwtService) {
            const { jwtClientId, jwtServiceSecret } = this._ufinityConfig.jwt
            return this.fetch({
                url: ConfigHelper.getInstance().getConfig('urls').gatewayTokenApiUrl,
                method: 'POST',
                headers: {
                    'Content-Type':
                        Constants.httpHeader.ContentType.APPLICATION_WWW_FORM_URLENCODED,
                },
                body: {
                    grant_type: 'client_credentials',
                    client_id: jwtClientId,
                    client_secret: jwtServiceSecret,
                    service_id: serviceId,
                    service_type: serviceType,
                },
                log: 'GETTING_JWT_FROM_SERVICE',
                // TODO: for some reason destructuring a response object is not working here
            }).pipe(
                mergeMap((response) => {
                    if (!response.response.access_token) {
                        return throwError(
                            new VoltError(VoltError.codes.PAYMENT_TOKEN_ERROR, {
                                extraLog: 'Missing access token in the response',
                            })
                        )
                    }
                    return of(response.response.access_token)
                }),
                catchError(() =>
                    throwError(
                        new VoltError(VoltError.codes.PAYMENT_TOKEN_ERROR, {
                            extraLog: 'Errror while retrieving payment gateway token',
                        })
                    )
                )
            )
        }
        return new Promise((res, rej) => {
            const { iss = '', privateKey = '' } = this._ufinityConfig.jwt

            this.logger.info(`Generating JWT`)

            if (!iss && !privateKey) return

            const jwt = generateJwtForOtt({
                serverTimeStampOffset: this.serverTimeStampOffset,
                accountId: serviceId,
                accountType: serviceType,
                privateKey,
                iss,
            })

            this.logger.info('JWT generated')
            res(jwt)
        })
    }

    /**
     * Gets user's device attributes
     * @returns {DeviceAttributes}
     * @private
     */
    _getDeviceAttributes() {
        const { platform, publicIp } = this.config
        return {
            device: platform === Constants?.platform?.smartTvOs ? 'smartTv' : 'web', // TODO: create a function which returns user's caller device
            deviceId: this._deviceId,
            userIpAddress: publicIp || '1.1.1.1',
        }
    }

    /**
     * Wraps methods for generating JWT OTT Token
     * @returns {String} JWT
     */
    _getJwt({ serviceType, serviceId }, useCache = false) {
        // / ! \ Very important / ! \
        // serviceId and serviceType SHALL be the same for the TOKEN using against all endpoints
        // Example: if reset Pin requiert serviceType = 'TVID' then Token used by this API SHALL be generated same serviceType
        if (!serviceType || !serviceId) {
            return throwError(
                new VoltError(VoltError.codes.AUTH_UNAUTHORIZED_USER, {
                    extraLog: 'Absence of serviceId / serviceType',
                })
            )
        }

        return (
            useCache
                ? DataHelper.getInstance()
                      .readData([DataHelper.STORE_KEY.GATEWAY_API_TOKEN])
                      .pipe(
                          switchMap(() =>
                              of(
                                  DataHelper.getInstance().getData(
                                      DataHelper.STORE_KEY.GATEWAY_API_TOKEN
                                  )
                              )
                          )
                      )
                : of(null)
        ).pipe(
            switchMap((cachedJwt) => {
                if (cachedJwt) return of(cachedJwt)
                return from(this._generateJwt({ serviceType, serviceId })).pipe(
                    mergeMap((jwt) => {
                        return DataHelper.getInstance()
                            .storeData([[DataHelper.STORE_KEY.GATEWAY_API_TOKEN, jwt]])
                            .pipe(switchMap(() => of(jwt)))
                    }),
                    catchError(() => {
                        this.logger.error('Error while generating JWT.')
                        return throwError(
                            new VoltError(VoltError.codes.PAYMENT_TOKEN_ERROR, {
                                extraLog: 'Cannot generate payment token',
                            })
                        )
                    })
                )
            })
        )
    }

    // ---------- AUTH FLOW ----------

    /**
     * "Notify User Login" is used for storing user logins in a database for history
     * @returns {Observable<Boolean>} in case of success returns true, otherwise returns false
     */
    notifyUserLogin() {
        if (this._ufinityConfig.disableNotifyUserLogin) return of(true)
        const { essoid, tvid, hubId } = this._getUserProfileData()
        const { serviceType, serviceId } = this._getServiceIdAndType({ tvid, essoid })
        const deviceAttributes = this._getDeviceAttributes()

        this.logger.info('Sending "Notify User Login" request...')
        return this._getJwt({ serviceType, serviceId }).pipe(
            mergeMap((jwt) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/notify/login`,
                    body: {
                        serviceId,
                        serviceType,
                        hubId,
                        ...deviceAttributes,
                    },
                    headers: {
                        'Content-type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        Authorization: `Bearer ${jwt}`,
                    },
                    method: 'POST',
                    log: 'NOTIFY_LOGIN',
                })
            }),
            mergeMap(() => of(true)),
            catchError((error) => {
                this.logger.warn('Received and error while notifying login', error)
                return of(false)
            })
        )
    }

    //TODO: change JWT generation exactly for this method
    /**
     * Implements the "Retrieve Mapping Between Hub iD and TV ID" API method
     * @param {String} essoId
     * @returns {Observable<Array<TvService>>} in case of success returns array of tv ids
     *  otherwise returns an error
     */
    retrieveMappingBetweenHubIdAndTvId(essoId) {
        this.logger.info('Sending "Retrieve Mapping Between Hub iD and TV ID" request...')
        return from(this._generateJwt(this._getServiceIdAndType({ essoid: essoId }))).pipe(
            mergeMap((jwt) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/account/mapping/${essoId}`,
                    headers: {
                        'Content-type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        Authorization: `Bearer ${jwt}`,
                    },
                    method: 'GET',
                    log: 'RETRIEVE_MAPPING_BETWEEN_HUBID_AND_TVID',
                }).pipe(switchMap(({ response = {} }) => of(tvServiceFactory(response))))
            }),
            catchError((error) =>
                throwError(
                    new VoltError(
                        error.code === VoltError.codes.UNKNOWN_API_ERROR.code
                            ? VoltError.codes.ERROR_RETRIEVING_TV_SERVICES
                            : error,
                        {
                            inheritedError: error,
                        }
                    )
                )
            )
        )
    }

    // ---------- PIN FLOW ----------

    /**
     * Implements the "Reset Parental Pin for Service ID" API method
     * @returns {Observable<Boolean>} returns true if resetting was successful
     */
    resetParentalPin() {
        const { tvid, hubId } = this._getUserProfileData()
        const { serviceType, serviceId } = this._getServiceIdAndType({ tvid }) // SHALL be on Nagra Account, then we shall use TVID
        const deviceAttributes = this._getDeviceAttributes()

        this.logger.info('Sending "Reset Parental Pin for Service ID" request...')
        return this._getJwt({ serviceType, serviceId }).pipe(
            mergeMap((jwt) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/pin/parental`,
                    body: {
                        serviceId,
                        serviceType,
                        hubId,
                        emailAddress: hubId,
                        ...deviceAttributes,
                    },
                    headers: {
                        'Content-type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        Authorization: `Bearer ${jwt}`,
                    },
                    method: 'POST',
                    log: 'RESET_PARENTAL_PIN',
                }).pipe(
                    mergeMap(() => of(true)),
                    catchError((error) =>
                        throwError(
                            new VoltError(
                                error.code === VoltError.codes.UNKNOWN_API_ERROR.code
                                    ? VoltError.codes.ERROR_RESET_PIN
                                    : error,
                                {
                                    inheritedError: error,
                                }
                            )
                        )
                    )
                )
            })
        )
    }

    /**
     * Implements the "Get R21 Pin for Service ID" API method
     * @returns {Observable<Boolean>} returns true if resetting was successful
     */
    resetR21Pin() {
        const { tvid, hubId } = this._getUserProfileData()
        const { serviceType, serviceId } = this._getServiceIdAndType({ tvid }) // SHALL be on Nagra Account, then we shall use TVID
        const deviceAttributes = this._getDeviceAttributes()

        this.logger.info('Sending "Get R21 Pin for Service ID" request...')
        return this._getJwt({ serviceType, serviceId }).pipe(
            mergeMap((jwt) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/pin/r21`,
                    body: {
                        serviceId,
                        serviceType,
                        hubId,
                        emailAddress: hubId,
                        ...deviceAttributes,
                    },
                    headers: {
                        'Content-type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        Authorization: `Bearer ${jwt}`,
                    },
                    method: 'POST',
                    log: 'RESET_CENSORSHIP_PIN',
                }).pipe(
                    mergeMap(() => of(true)),
                    catchError((error) =>
                        throwError(
                            new VoltError(
                                error.code === VoltError.codes.UNKNOWN_API_ERROR.code
                                    ? VoltError.codes.ERROR_RESET_PIN
                                    : error,
                                {
                                    inheritedError: error,
                                }
                            )
                        )
                    )
                )
            })
        )
    }

    // ---------- PURCHASE FLOW ----------

    //TODO: seems like callback hell. Will try to refactor this later
    /**
     * Retrieves the list of Ufinity Products from Nagra Products
     * @param {Array<String>} nagraProductIds
     * @returns {Observable<Array<UfinityProduct>>} Ufinity Products
     */
    getUfinityOffersFromNagraProducts(nagraProductIds = []) {
        if (this._ufinityConfig.disableOfferDetails) {
            return of({})
        }

        this.logger.info(`Retrieve Product Details...`)
        return this.getUfinityProductsFromNagraProducts(nagraProductIds).pipe(
            mergeMap((mappingObject = {}) => {
                const ufinityProductsIds = Object.values(mappingObject).flat()
                if (!ufinityProductsIds.length) {
                    return of({})
                }
                return this.getUfinityOffers(ufinityProductsIds).pipe(
                    mergeMap((offers) => {
                        return of(
                            // There can be duplicates so this code return an array of uniq offers
                            Object.keys(mappingObject).reduce((acc, nagraProductId) => {
                                const offerIds = mappingObject[nagraProductId] || []
                                acc[nagraProductId] = offers.filter((offer) =>
                                    offerIds.includes(offer.id)
                                )
                                return acc
                            }, {})
                        )
                    })
                )
            })
        )
    }

    /**
     * Transform an array of product IDs to a string of product IDs separated by comma
     * It needs for the request parameters
     * @param {Array<String>} productIds array of product IDs
     * @returns {String} string of product IDs separated by comma
     */
    _getProductsString(productIds) {
        if (!productIds.length) {
            return throwError(
                new VoltError(VoltError.codes.MISSING_PRODUCT, {
                    extraLog: 'There is no products',
                })
            )
        }
        return of((Array.isArray(productIds) ? productIds : [productIds]).join(','))
    }

    /**
     * Retrieves the list of Ufinity Products from Nagra Products
     * @param {Array<String>} ufinityProductIds
     * @returns {Observable<Array<UfinityProduct>>} Ufinity Products
     */
    getUfinityOffers(ufinityProductIds = []) {
        this.logger.info(
            `Retrieve PMP Products details from Ufinity Products: ${ufinityProductIds}...`
        )

        return this._getProductsString(ufinityProductIds).pipe(
            mergeMap((productIdsString) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/product/${productIdsString}`,
                    method: 'GET',
                    log: 'GET UFINITY PRODUCT DETAILS',
                })
            }),
            switchMap(({ response = {} }) => {
                const offers = ufinityOffersFactory(
                    response.productIdList,
                    CommonApi.getInstance().getPaymentMethodsTypes()
                )
                return of(offers)
            })
        )
    }

    /**
     * Retrieves the list of Ufinity Products from Nagra Products
     * @param {Array<String>} nagraProductIds
     * @returns {Observable<UfinityProductMapping>} returns mapping of ufinity products. See {@link ufinityProductMappingFactory}
     */
    getUfinityProductsFromNagraProducts(nagraProductIds = []) {
        this.logger.info(`Retrieve PMP Products with Nagra Products ${nagraProductIds}...`)

        return this._getProductsString(nagraProductIds).pipe(
            mergeMap((productIdsString) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/product/nagra/${productIdsString}`,
                    method: 'GET',
                    log: 'GET UFINITY PRODUCT FROM NAGRA PRODUCTS',
                })
            }),
            switchMap(({ response = {} }) =>
                of(ufinityProductMappingFactory(response.productIdList))
            )
        )
    }

    /**
     * Request to purchase a TVOD product
     * @param {String} productId
     * @param {String} offerId
     * @param {PurchaseData} purchaseData
     * @returns {Observable<Boolean>} returns true if the purchase was successful
     */
    purchaseTVOD(productId, offerId, purchaseData = {}) {
        const { tvid, hubId } = this._getUserProfileData()
        const { serviceType, serviceId } = this._getServiceIdAndType({ tvid })
        const deviceAttributes = this._getDeviceAttributes()
        const { offerTitle = 'title', offerType = 'title' } = purchaseData

        this.logger.info(
            `Purchasing TVOD [NAGRA Product]: ${productId} [UFINITY Product]: ${offerId}... `
        )
        return this._getJwt({ serviceType, serviceId }).pipe(
            mergeMap((jwt) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/purchase/bill/cdr`,
                    headers: {
                        'Content-type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        Authorization: `Bearer ${jwt}`,
                    },
                    body: {
                        serviceId,
                        serviceType,
                        hubId,
                        ...deviceAttributes,
                        productId: offerId,
                        productType: offerType,
                        externalProductId: productId,
                        billRemark: offerTitle ? offerTitle : `TVOD Rental`,
                    },
                    method: 'POST',
                    log: 'PURCHASE_TVOD',
                }).pipe(
                    mergeMap(({ response = {} } = {}) =>
                        of(response.code && response.code === 'OK')
                    )
                )
            }),
            catchError((error) =>
                throwError(
                    new VoltError(
                        error.code === VoltError.codes.UNKNOWN_API_ERROR.code
                            ? VoltError.codes.PURCHASE_FAILED
                            : error,
                        {
                            inheritedError: error,
                        }
                    )
                )
            )
        )
    }

    /**
     * Request to purchase a subscription product
     * @param {String} productId
     * @param {String} offerId
     * @param {PurchaseData} purchaseData
     * @returns {Observable<Boolean>} returns true if the purchase subscription was successful`
     */
    subscribe(productId, offerId, purchaseData = {}) {
        const { tvid, hubId, essoid } = this._getUserProfileData()
        const deviceAttributes = this._getDeviceAttributes()
        const { offerTitle, offerType = 'UNKNOWN' } = purchaseData

        const isInternetPurchase =
            (typeof offerId === 'string' && offerId.toLowerCase().includes('intv')) ||
            offerType === UfinityProduct.PRODUCT_TYPE.INTERNET
        // Absolutely mandatory: INTERNET pack SHALL be purchased using ESSOID based on Specification while CATV SHALL rely on TVID
        const { serviceType, serviceId } = isInternetPurchase
            ? this._getServiceIdAndType({ essoid })
            : this._getServiceIdAndType({ tvid })

        const purchase_endpoint = isInternetPurchase ? 'intv' : 'catv'
        this.logger.info(
            `Purchasing ${purchase_endpoint} PACK [NAGRA Product]: ${productId} [UFINITY Product]: ${offerId} / ${offerType}... using Service Type: ${serviceType}`
        )

        return this._getJwt({ serviceType, serviceId }).pipe(
            mergeMap((jwt) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/purchase/bill/${purchase_endpoint}`,
                    headers: {
                        'Content-type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        Authorization: `Bearer ${jwt}`,
                    },
                    body: {
                        serviceId,
                        serviceType,
                        hubId,
                        ...deviceAttributes,
                        productId: offerId,
                        productType: offerType,
                        externalProductId: productId,
                        billRemark: offerTitle ? offerTitle : `Subscription to ${offerType} Pack`,
                    },
                    method: 'POST',
                    log: 'SUBSCRIBE',
                }).pipe(
                    mergeMap(({ response = {} } = {}) => {
                        return of(response.code && response.code === 'OK')
                    }),
                    catchError((error) =>
                        throwError(
                            new VoltError(
                                error.code === VoltError.codes.UNKNOWN_API_ERROR.code
                                    ? VoltError.codes.PURCHASE_FAILED
                                    : error,
                                {
                                    inheritedError: error,
                                }
                            )
                        )
                    )
                )
            })
        )
    }

    /**
     * Notify purchase of entitlements from Apple AppStore
     *
     * TODO: not testable for now as purchase through the store is not implemented
     *
     * @param {Object} options
     * @param {string} options.receipt the base64 encoded receipt data
     * @param {string} options.productId product ID from
     * @returns {Observable<boolean>}
     */
    notifyPurchaseEntitlement({ receipt, productId, purchaseData = {} } = {}) {
        const { tvid, hubId } = this._getUserProfileData()
        const { serviceType, serviceId } = this._getServiceIdAndType({ tvid })
        const deviceAttributes = this._getDeviceAttributes()

        this.logger.info('Sending notify purchase of entitlements from Apple AppStore')
        return this._getJwt({ serviceType, serviceId }).pipe(
            mergeMap((jwt) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/notify/ios/status`,
                    body: {
                        serviceId,
                        serviceType,
                        hubId,
                        ...deviceAttributes,
                        productId,
                        receipt,
                        currencyCode: purchaseData.currency,
                        iosPrice: purchaseData.price,
                    },
                    headers: {
                        'Content-type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        Authorization: `Bearer ${jwt}`,
                    },
                    method: 'POST',
                    log: 'NOTIFY_APPLE_STORE_PURCHASE',
                }).pipe(mergeMap(() => of(true)))
            })
        )
    }

    redeemVoucherCode(code) {
        const deviceAttributes = this._getDeviceAttributes()
        const { essoid, tvid, hubId } = this._getUserProfileData()
        const { serviceType, serviceId } = this._getServiceIdAndType({ tvid, essoid })
        return this._getJwt({ serviceType, serviceId }).pipe(
            mergeMap((jwt) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/voucher/redeem`,
                    headers: {
                        'Content-type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        Authorization: `Bearer ${jwt}`,
                    },
                    body: {
                        serviceId,
                        serviceType,
                        hubId,
                        ...deviceAttributes,
                        voucherCode: code,
                    },
                    method: 'POST',
                    log: 'REDEEM_CODE',
                }).pipe(
                    mergeMap(({ response = {} } = {}) =>
                        of(response.code && response.code === 'OK')
                    )
                )
            }),
            catchError((err) => throwError(err))
        )
    }

    transactionList(fromDate, toDate) {
        const { essoid, tvid } = this._getUserProfileData()
        const { serviceType, serviceId } = this._getServiceIdAndType({ tvid, essoid })
        return this._getJwt({ serviceType, serviceId }).pipe(
            mergeMap((jwt) => {
                return this.fetch({
                    url: `${this._apiUrl}/api/v1/transaction/${tvid}/from/${fromDate}/to/${toDate}`,
                    headers: {
                        'Content-type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        Authorization: `Bearer ${jwt}`,
                    },
                    method: 'GET',
                }).pipe(
                    switchMap(({ response = {} }) =>
                        of(transactionListFactory(response.transactionHistoryList))
                    )
                )
            }),
            catchError((err) => throwError(err))
        )
    }
}

/**
 * Typical request headers
 * @typedef {Object} RequestHeaders
 * @property {String} Content-type - request's content type
 * @property {String} Authorization - request's authorization header
 */

/**
 * Properties of a caller device
 * @typedef {Object} DeviceAttributes
 * @property {String} device - friendly description of the device the user signs on with
 * @property {String} deviceId - id of the user's device
 * @property {String} userIpAddress - user’s IP Address
 */

/**
 * User's profile data
 * @typedef {Object} ProfileData
 * @property {String} essoid - esso id
 * @property {String} tvid - tv id
 * @property {String} hubId - hub id
 */

/**
 * An additional data for service: service type and service id
 * @typedef {Object} Service
 * @property {String} serviceType - type of a service: TVID or ESSOID
 * @property {String} serviceId - service id
 */
